import { ProcessType, getProcessType } from './request';
import { MetadataFilter } from './metadata-filter';

export abstract class IFileInfo {
  project!: Project;
  name!: string;
  type!: string;
  relPath!: string;
  rawData!: RawFileInfo;
  results?: boolean = false;

  public constructor(init?: Partial<IFileInfo>) {
    Object.assign(this, init);
  }
}

export abstract class IFile extends IFileInfo {
  size!: number;
  fileExtension!: string | null;
}

export class ImageFile extends IFile {
  height!: number;
  width!: number;

  public constructor(rawData: any, results: boolean) {
    super();
    this.rawData = rawData;
    Object.assign(this, <Partial<FileProject>>rawData);
    this.results = results;
    this.fileExtension = getFileExtension(this.relPath);
  }
}

export class BasicFile extends IFile {
  public constructor(rawData: any, results: boolean) {
    super();
    Object.assign(this, <Partial<FileProject>>rawData);
    this.rawData = rawData;
    this.results = results;
    this.fileExtension = getFileExtension(this.relPath);
  }
}

/*
(?:         # begin non-capturing group
  \.        #   a dot
  (         #   begin capturing group (captures the actual extension)
    [^.]+   #     anything except a dot, multiple times
  )         #   end capturing group
)?          # end non-capturing group, make it optional
$           # anchor to the end of the string
 */
const re = /(?:\.([^.]+))?$/;
function getFileExtension(fileName: string | undefined): string | null {
  const extension = (fileName) ? re.exec(fileName) : null;
  if (extension) {
    return extension[1];
  } else {
    return extension;
  }
}

export class FileFolder extends IFileInfo {
  processType!: ProcessType;

  public constructor(rawData: any, resultsFolder: string) {
    super();
    Object.assign(this, <Partial<FileProject>>rawData);
    this.rawData = rawData;
    this.results = !!(resultsFolder);
    this.processType = getProcessType(resultsFolder);
  }
}

export class FileProject extends IFileInfo {
  public constructor(rawData: any) {
    super();
    Object.assign(this, <Partial<FileProject>>rawData);
    this.rawData = rawData;
  }
}

export class RawFile extends IFileInfo {
  public constructor(rawData: any) {
    super();
    Object.assign(this, <Partial<FileProject>>rawData);
    this.rawData = rawData;
  }
}

export class RawFileInfo {
  width?: number = 0;
  height?: number = 0;
  supportedImage?: boolean = false;
  size?: number | null = 0;
  resultFile?: boolean = false;
  resultsFolder?: ProcessType | null = null;
  resultsSummary?: string | null = null;
  processNumber?: number = 0;
  project!: Project;
  name!: string;
  type!: string;
  relPath!: string;
  bucket?: string;
}

export class Project {
  id!: number;
  name!: string;
  bucket!: string;

  public constructor(init?: Partial<Project>) {
    Object.assign(this, init);
  }

  static toRawFileInfo(proj: Project): RawFileInfo {
    const info: RawFileInfo = {
      name: proj.name,
      type: 'Project',
      relPath: '/',
      project: proj,
    };
    return info;
  }

  toFileInfo(): RawFileInfo {
    return Project.toRawFileInfo(this);
  }
}

export class DiskUsage {
  project!: Project;
  used!: number;
  total!: number;
}

export enum DisplayType {
  Diagram = 'Diagram',
  /** A spatial-omics dataset in the visualizer's Spatial omics plot mode. */
  SpatialOmics = 'SpatialOmics',
  /** Annotations opened by themselves (a label volume, a GeoJSON folder…) in the visualizer. */
  Annotations = 'Annotations',
  ResultsSummary = 'ResultsSummary',
  Code = 'Code',
  TabDelimited = 'TabDelimited',
  None = 'None',
}

export class ImageInfo {
  isGrayscale!: boolean;
  trueImageSize!: number[];
  urls!: string[];
  // Optional small-tier URLs that map 1:1 to `urls`. When present, the
  // diagram component renders these first as a fast blurry placeholder,
  // then transparently swaps in the large tier from `urls` once it loads.
  // Each entry is the same image at the same z-slice but generated at the
  // backend's PREVIEW_SMALL_SIZE (default 128 px).
  smallUrls?: string[];
  isStack!: boolean;
  showStack!: boolean;
  scaleRatio!: boolean;
  fileName!: string;
  roiJsonStr!: string | undefined;
  // Per-slice ROI GeoJSON, aligned 1:1 with `urls` — one entry per slice-file
  // in a folder stack (null where a slice has no sibling geojson). The viewer
  // applies roiJsonStrs[z] for the displayed slice and swaps it on scrub. See
  // IImageInfo.roiJsonStrs (jit-ui#93).
  roiJsonStrs?: (string | null)[];
  // jit-service's region source for a large sibling geojson (`/regions/{id}/`),
  // instead of its text: the viewer fetches the tiles in view at the zoom's level
  // of detail (IImageInfo.regionSourceUrl).
  regionSourceUrl?: string;
  imageMeta!: ImageMetadata[];
  // One-shot hint consumed by VisualizerComponent: open the stack on this
  // slice instead of 0 (e.g. the user clicked "image_5" within a numbered
  // series, so the viewer should open on that slice, not the start).
  initialZIndex?: number;
  // false for a numbered-series stack assembled from separate files (each
  // urls[z] is its own complete, directly-loadable preview) — true/unset
  // (default) for a single server-tiled file with an internal z dimension.
  tiled?: boolean;
}

export class FileMetadata {
  bitsPerPixel!: number;
  fileName!: string;
  imageCount!: number;
  // A real array, not a fixed 1-tuple: a server response that can't read
  // dimensions for a file (e.g. a gzip-compressed TIFF) sends `[]`, and
  // FilesService.buildSingleFileImageInfo() must be able to detect that
  // rather than have `.length === 0` type-checked away as unreachable.
  imageMeta!: ImageMetadata[];

  zoomStatus!: ZoomStatus;

  public toString(): string {
    return `bitsPerPixel: ${this.bitsPerPixel}, fileName: ${this.fileName}, imageCount: ${this.imageCount},
          imageMetadata: ${this.imageMeta}`;
  }
}

export class ImageMetadata {
  channelCount!: number;
  rgbChannels!: number;
  x!: number;
  y!: number;
  z!: number;
  /** Physical pixel size in micrometres (µm/pixel); null/undefined when the
   *  image format reports no length-based physical size. Normalised to µm from
   *  whatever unit Bio-Formats reports — the UI picks the display unit by
   *  magnitude (nm/µm/mm/...). Used by the OSD scale bar; available to Plotly
   *  and the Region Editor (e.g. region areas in µm²). */
  mppX?: number;
  mppY?: number;
}

export class ZoomStatus {
  cached!: false;
  fileSize!: number;
  lifetime!: number;
  localPath!: string;
  timeStamp!: number;
}

export enum PatternType {
  WILDCARD = 'WILDCARD',
  REGEX = 'REGEX'
}

export interface FileSearchRequest {
  project:           Project;
  subPath?:          string;
  pattern:           string;
  caseSensitive:     boolean;
  patternType:       PatternType;
  metadataCriteria?: MetadataFilter[];
}
